<template>
  <div>
    <p v-if="loading"><Loading /></p>

    <div class="page">
      <!-- Branch Code Input Form -->
      <div v-if="!missions" class="form">
        <div class="field">
          <label class="field__label field--required">分店代碼</label>
          <input
            class="field__input"
            v-model="branchCode"
            placeholder="請輸入分店代碼"
            :disabled="loading"
          />
          <small class="field__desc">請輸入您的分店代碼以產生各任務 QR Code</small>
        </div>

        <SharedButton
          class="s-btn s-btn-bg-primary s-mt-3"
          :disabled="loading || !branchCode"
          @click="fetchAllMissionsTokens"
        >
          {{ loading ? '產生中...' : '產生 QR Code' }}
        </SharedButton>
      </div>

      <!-- Missions Accordion -->
      <div v-else class="missions">
        <div
          v-for="mission in missions"
          :key="mission.id"
          class="mission-item"
        >
          <div class="mission-item__header" @click="toggleMission(mission.id)">
            <span class="mission-item__title">{{ mission.title }}</span>
            <span class="mdi" :class="isOpen(mission.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
          </div>
          <div v-if="isOpen(mission.id)" class="mission-item__body">
            <div class="d-flex justify-content-center">
              <vue-qr :text="getMissionQrUrl(mission)" :size="250"></vue-qr>
            </div>
            <div v-if="mission.confirm_type === 'dynamic_verification'" class="text-center">
              <small class="text-muted">QR Code 將在 {{ countdown }} 秒後自動更新</small>
            </div>
          </div>
        </div>

        <SharedButton
          class="s-btn s-btn-bg-primary s-mt-3"
          @click="reset"
        >
          重新輸入分店代碼
        </SharedButton>
      </div>
    </div>
  </div>
</template>

<script>
import VueQr from 'vue-qr'
import { mapGetters } from "vuex";
import Loading from '@/components/Page/Liff/Shared/Loading'
import SharedButton from "@/components/Page/Liff/Shared/Button";
import rewardsCardApi from "@/apis/liff/v2/rewards-card";

export default {
  name: 'RewardMissionAllMissions',
  components: {
    VueQr,
    Loading,
    SharedButton,
  },
  data() {
    return {
      branchCode: '',
      missions: null,
      loading: false,
      openMissionIds: [],
      countdown: 30,
      countdownIntervalId: null,
      refreshInterval: 30,
    }
  },
  computed: {
    ...mapGetters({
      liffId: "liffGeneral/liffId",
    }),
    rewardCardId() {
      return this.$route.params.rewardCardId
    },
  },
  methods: {
    async fetchAllMissionsTokens() {
      this.loading = true
      try {
        this.missions = await rewardsCardApi.generateAllMissionsTokens(this.rewardCardId, {
          branch_code: this.branchCode
        })
        this.openMissionIds = this.missions.map(m => m.id)
        if (this.missions.some(m => m.confirm_type === 'dynamic_verification')) {
          this.startCountdown()
        }
      } catch (error) {
        this.$swal.fire({
          title: error.response?.data?.message || '產生 QR Code 失敗，請重試',
          type: "error",
        });
      } finally {
        this.loading = false
      }
    },
    async refreshDynamicTokens() {
      try {
        const fresh = await rewardsCardApi.generateAllMissionsTokens(this.rewardCardId, {
          branch_code: this.branchCode
        })
        this.missions = this.missions.map(m => {
          const updated = fresh.find(f => f.id === m.id)
          return updated ? { ...m, token: updated.token } : m
        })
      } catch (error) {
        // silently ignore refresh errors
      }
    },
    startCountdown() {
      this.countdown = this.refreshInterval
      this.countdownIntervalId = setInterval(async () => {
        this.countdown--
        if (this.countdown === 0) {
          clearInterval(this.countdownIntervalId)
          await this.refreshDynamicTokens()
          this.startCountdown()
        }
      }, 1000)
    },
    stopCountdown() {
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId)
        this.countdownIntervalId = null
      }
    },
    getMissionQrUrl(mission) {
      if (mission.confirm_type === 'dynamic_verification') {
        return `https://liff.line.me/${this.liffId}/rewards-card/mission/${mission.id}?token=${mission.token}`
      }
      return `https://liff.line.me/${this.liffId}/rewards-card/mission/${mission.id}`
    },
    toggleMission(id) {
      const idx = this.openMissionIds.indexOf(id)
      if (idx === -1) {
        this.openMissionIds.push(id)
      } else {
        this.openMissionIds.splice(idx, 1)
      }
    },
    isOpen(id) {
      return this.openMissionIds.includes(id)
    },
    reset() {
      this.stopCountdown()
      this.missions = null
      this.branchCode = ''
      this.openMissionIds = []
    },
  },
  beforeDestroy() {
    this.stopCountdown()
  },
}
</script>

<style lang="scss" scoped>
.page {
  padding: 16px;
}

.form {
  padding: 20px 10px;
  background: #ffffff;
}

.field {
  &:not(:last-of-type) {
    margin-bottom: 24px;
  }

  &__label {
    font-weight: 400;
    font-size: 14px;
    color: var(--liff-primary_text_color);
    margin-bottom: 8px;
    display: block;
  }

  &__desc {
    color: var(--liff-secondary_text_color);
    font-size: 12px;
    line-height: 1;
    margin-top: 4px;
  }

  &__input {
    font-weight: 500;
    font-size: 16px;
    color: var(--liff-primary_text_color);
    border: 1px solid #e5e5ea;
    border-radius: 5px;
    background: transparent;
    padding: 8px 12px;
    width: 100%;

    &::placeholder {
      color: #b7b7b7;
    }

    &:focus {
      border: 1px solid var(--liff-primary_text_color);
      outline: none;
    }

    &:disabled {
      background-color: #f0f0f0;
    }
  }

  &--required {
    &:after {
      content: "*";
      position: relative;
      font-family: "PingFang TC";
      color: var(--s-primary);
      margin-left: 2px;
    }
  }
}

.missions {
  padding: 10px;
}

.mission-item {
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5ea;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: #1e88e5;
    color: #ffffff;
    cursor: pointer;
    user-select: none;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
  }

  &__body {
    padding: 16px;
    background-color: #e8f5e9;
  }
}
</style>
